import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import {
  Save as SaveIcon,
  Settings as SettingsIcon,
  BarChart as StatsIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as yup from "yup";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api";

const getAuthToken = () => {
  const adminToken = localStorage.getItem("admin_token");
  const userToken = localStorage.getItem("user_token");
  const legacyToken = localStorage.getItem("token");
  return adminToken || legacyToken || userToken;
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const validationSchema = yup.object({
  default_user_points: yup.number().min(0, "Must be >= 0").required("Required"),
  max_radius_km: yup
    .number()
    .min(1, "Must be >= 1")
    .max(100, "Must be <= 100")
    .required("Required"),
});

const Settings = () => {
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(0);

  const {
    data: settings,
    isLoading,
    error,
  } = useQuery("settings", async () => {
    const response = await axios.get(`${API_BASE_URL}/settings`, {
      headers: getAuthHeaders(),
    });
    return response.data.settings;
  });

  const { data: stats } = useQuery("settings-stats", async () => {
    const response = await axios.get(`${API_BASE_URL}/settings/stats`, {
      headers: getAuthHeaders(),
    });
    return response.data.stats;
  });

  const updateMutation = useMutation(
    async (values) => {
      const response = await axios.put(`${API_BASE_URL}/settings`, values, {
        headers: getAuthHeaders(),
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success("Settings updated successfully");
        queryClient.invalidateQueries("settings");
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.message || "Failed to update settings",
        );
      },
    },
  );

  const formik = useFormik({
    initialValues: {
      app_name: settings?.app_name || "LOCSY",
      maintenance_mode: settings?.maintenance_mode || false,
      registration_enabled:
        settings?.registration_enabled !== undefined
          ? settings.registration_enabled
          : true,
      email_notifications:
        settings?.email_notifications !== undefined
          ? settings.email_notifications
          : true,
      sms_notifications: settings?.sms_notifications || false,
      default_user_points: settings?.default_user_points || 100,
      max_radius_km: settings?.max_radius_km || 50,
      currency: settings?.currency || "INR",
      timezone: settings?.timezone || "Asia/Kolkata",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      updateMutation.mutate(values);
    },
    enableReinitialize: true,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">Failed to load settings. Please try again.</Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
        Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage system settings and configurations
      </Typography>

      <Card>
        <CardContent>
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            sx={{ mb: 3 }}
          >
            <Tab
              label="General Settings"
              icon={<SettingsIcon />}
              iconPosition="start"
            />
            <Tab
              label="System Statistics"
              icon={<StatsIcon />}
              iconPosition="start"
            />
            <Tab
              label="Security & Legal"
              icon={<SecurityIcon />}
              iconPosition="start"
            />
          </Tabs>

          {tabValue === 0 && (
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Application Settings
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="app_name"
                    name="app_name"
                    label="Application Name"
                    value={formik.values.app_name}
                    onChange={formik.handleChange}
                    margin="normal"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="currency"
                    name="currency"
                    label="Currency"
                    value={formik.values.currency}
                    onChange={formik.handleChange}
                    margin="normal"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="timezone"
                    name="timezone"
                    label="Timezone"
                    value={formik.values.timezone}
                    onChange={formik.handleChange}
                    margin="normal"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    User Settings
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="default_user_points"
                    name="default_user_points"
                    label="Default User Points (Signup Bonus)"
                    type="number"
                    value={formik.values.default_user_points}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.default_user_points &&
                      Boolean(formik.errors.default_user_points)
                    }
                    helperText={
                      formik.touched.default_user_points &&
                      formik.errors.default_user_points
                    }
                    margin="normal"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="max_radius_km"
                    name="max_radius_km"
                    label="Max Radius (km)"
                    type="number"
                    value={formik.values.max_radius_km}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.max_radius_km &&
                      Boolean(formik.errors.max_radius_km)
                    }
                    helperText={
                      formik.touched.max_radius_km &&
                      formik.errors.max_radius_km
                    }
                    margin="normal"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Feature Toggles
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        id="maintenance_mode"
                        name="maintenance_mode"
                        checked={formik.values.maintenance_mode}
                        onChange={formik.handleChange}
                      />
                    }
                    label="Maintenance Mode"
                    helperText="When enabled, the system will be in maintenance mode"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        id="registration_enabled"
                        name="registration_enabled"
                        checked={formik.values.registration_enabled}
                        onChange={formik.handleChange}
                      />
                    }
                    label="Registration Enabled"
                    helperText="Allow new user registrations"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Notification Settings
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        id="email_notifications"
                        name="email_notifications"
                        checked={formik.values.email_notifications}
                        onChange={formik.handleChange}
                      />
                    }
                    label="Email Notifications"
                    helperText="Enable email notifications for users"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        id="sms_notifications"
                        name="sms_notifications"
                        checked={formik.values.sms_notifications}
                        onChange={formik.handleChange}
                      />
                    }
                    label="SMS Notifications"
                    helperText="Enable SMS notifications for users"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<SaveIcon />}
                      disabled={updateMutation.isLoading}
                    >
                      {updateMutation.isLoading ? (
                        <CircularProgress size={24} />
                      ) : (
                        "Save Settings"
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          )}

          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                System Statistics
              </Typography>
              <Divider sx={{ mb: 3 }} />

              {stats && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography
                          color="text.secondary"
                          gutterBottom
                          variant="body2"
                        >
                          Total Users
                        </Typography>
                        <Typography variant="h4">
                          {stats.total_users || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography
                          color="text.secondary"
                          gutterBottom
                          variant="body2"
                        >
                          Total Shops
                        </Typography>
                        <Typography variant="h4">
                          {stats.total_shops || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography
                          color="text.secondary"
                          gutterBottom
                          variant="body2"
                        >
                          Total Bills
                        </Typography>
                        <Typography variant="h4">
                          {stats.total_bills || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography
                          color="text.secondary"
                          gutterBottom
                          variant="body2"
                        >
                          Active Coupons
                        </Typography>
                        <Typography variant="h4">
                          {stats.total_coupons || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography
                          color="text.secondary"
                          gutterBottom
                          variant="body2"
                        >
                          Active Job Alerts
                        </Typography>
                        <Typography variant="h4">
                          {stats.total_job_alerts || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}

          {tabValue === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Security & Legal Information
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Security Features
                </Typography>
                <Typography variant="body2">
                  • All user actions are logged in the audit log
                  <br />
                  • JWT token-based authentication
                  <br />
                  • Role-based access control (RBAC)
                  <br />
                  • Input validation and SQL injection prevention
                  <br />• XSS protection (React default)
                </Typography>
              </Alert>

              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Legal Disclaimer
                </Typography>
                <Typography variant="body2">
                  This system is protected by cyber laws. Unauthorized access is
                  prohibited and may result in legal action. All activities are
                  monitored and logged for security and compliance purposes.
                </Typography>
              </Alert>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Compliance & Regulations
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    • Data Protection: All user data is stored securely in
                    PostgreSQL database
                    <br />
                    • Audit Trail: All administrative actions are logged
                    <br />
                    • User Privacy: User data is only accessed by authorized
                    personnel
                    <br />
                    • Terms of Service: Users must agree to terms before using
                    the system
                    <br />• Privacy Policy: User data is handled according to
                    privacy policy
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Settings;
